<template>
  <v-row justify="center">
    <v-card class="transparent my-3 mx-auto" rounded outlined width="900">
      <v-card-text style="height: calc(100vh - 175px); overflow-y: auto;">
        <v-stepper class="transparent" v-model="currentStep" alt-labels>
          <v-stepper-header class="align-center">
            <img class="mt-n2 ml-6" :src="require('@/assets/appLogo-color--Onlight.svg')" width="100"/>

            <template v-for="(s, i) in visibleSteps">
              <v-divider v-if="i > 0" :key="`divider-${s.key}`" />
              <v-stepper-step :key="`step-${s.key}`" :step="i + 1" />
            </template>
          </v-stepper-header>

          <v-stepper-items>
            <!-- Step: charge_type (0th question) -->
            <v-stepper-content :step="stepNumber('charge_type')">
              <span class="d-block text-h5 font-weight-medium">¿Cómo te pagan los clientes?</span>
              <span class="d-block mb-5 body-1 text--primary">Esto define cómo va a funcionar Piriod para ti.</span>

              <v-row class="px-1">
                <v-col cols="4" v-for="(option, i) in chargeTypes" :key="`ct-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.charge_type === option.id }" @click="setResponse('charge_type', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-1 text--secondary font-weight-medium">{{ option.label }}</span>
                      <span class="d-block mt-1 body-2 text--primary">{{ option.description }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: active_clients (1st question - customer type & quantity) -->
            <v-stepper-content :step="stepNumber('active_clients')">
              <span class="d-block text-h5 font-weight-medium">¿A qué tipo de cliente le vendes?</span>
              <span class="d-block mb-5 body-1 text--primary">Selecciona el que describe a la mayoría.</span>

              <v-row class="px-1">
                <v-col cols="3" v-for="(option, i) in customerTypes" :key="`ctype-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.customer_type === option.id }" @click="setResponse('customer_type', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>

              <span class="d-block mt-8 text-h5 font-weight-medium">¿Cuántos clientes activos tienes hoy?</span>
              <span class="d-block mb-5 body-1 text--primary">Un rango aproximado es suficiente.</span>

              <v-row class="px-1">
                <v-col cols="3" v-for="(option, i) in activeClientsRanges" :key="`ac-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.active_clients === option.id }" @click="setResponse('active_clients', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: pricing_model (3rd question - if recurring or both) -->
            <v-stepper-content v-if="showPricingSteps" :step="stepNumber('pricing_model')">
              <span class="d-block text-h5 font-weight-medium">¿Cómo defines el precio?</span>
              <span class="d-block mb-5 body-1 text--primary">Cuéntanos cómo funciona hoy, aunque no tengas un modelo cerrado.</span>

              <v-row class="px-1">
                <v-col cols="4" v-for="(option, i) in pricingModels" :key="`pm-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.pricing_model === option.id }" @click="setResponse('pricing_model', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: billing_frequency (4th question - if recurring or both) -->
            <v-stepper-content v-if="showPricingSteps" :step="stepNumber('billing_frequency')">
              <span class="d-block text-h5 font-weight-medium">¿Con qué frecuencia cobras?</span>
              <span class="d-block mb-5 body-1 text--primary">Si usas varias, elige la más común.</span>

              <v-row class="px-1">
                <v-col cols="3" v-for="(option, i) in billingFrequencies" :key="`bf-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.billing_frequency === option.id }" @click="setResponse('billing_frequency', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: invoicing_method (5th question) -->
            <v-stepper-content :step="stepNumber('invoicing_method')">
              <span class="d-block text-h5 font-weight-medium">¿Cómo facturas hoy?</span>
              <span class="d-block mb-5 body-1 text--primary">Cuéntanos cómo lo haces hoy.</span>

              <v-row class="px-1">
                <v-col cols="4" v-for="(option, i) in invoicingMethods" :key="`im-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="py-4 px-2 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.invoicing_method === option.id }" @click="setResponse('invoicing_method', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: payment_method (6th question) -->
            <v-stepper-content :step="stepNumber('payment_method')">
              <span class="d-block text-h5 font-weight-medium">¿Cómo recibes el pago?</span>
              <span class="d-block mb-5 body-1 text--primary">Selecciona el método principal con el que recibes los pagos de tus clientes.</span>

              <v-row class="px-1">
                <v-col cols="4" v-for="(option, i) in paymentMethods" :key="`pme-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.payment_method === option.id }" @click="setResponse('payment_method', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>

            <!-- Step: payment_processor (7th question - only if card/both) -->
            <v-stepper-content v-if="showProcessorStep" :step="stepNumber('payment_processor')">
              <span class="d-block text-h5 font-weight-medium">¿Qué procesador de pagos usas o te gustaría usar?</span>
              <span class="d-block mb-5 body-1 text--primary">Si aún no tienes uno, no te preocupes, te ayudaremos a elegir.</span>

              <v-row class="px-1">
                <v-col cols="3" v-for="(option, i) in paymentProcessors" :key="`pp-${i}`">
                  <v-hover v-slot:default="{ hover }">
                    <v-sheet class="pa-4 text-center cursor-pointer" :class="{ 'v-sheet--active': responses.payment_processor === option.id }" @click="setResponse('payment_processor', option.id)" outlined rounded="lg" width="100%" :elevation="hover ? '3' : '0'">
                      <v-avatar color="indigo lighten-5" size="32"><v-icon color="primary">{{ option.icon }}</v-icon></v-avatar>
                      <span class="d-block mt-3 body-2 text--secondary font-weight-medium">{{ option.label }}</span>
                    </v-sheet>
                  </v-hover>
                </v-col>
              </v-row>
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
      </v-card-text>

      <v-card-actions class="pa-10">
        <v-btn class="pr-6 pl-3" @click="prevStep" v-if="currentStep > 1" large outlined :ripple="false">
          <v-icon left size="22">mdi-arrow-left</v-icon>Volver
        </v-btn>
        <v-spacer/>
        <span class="mr-6 red--text" v-if="showError">{{ errorMessage }}</span>
        <v-btn class="pr-4 pl-6" @click="nextStep" color="primary" large :ripple="false">
          {{ isLastStep ? 'Ver mi flujo personalizado' : 'Continuar' }}<v-icon dark right size="22">mdi-arrow-right</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-row>
</template>

<script>
import { mapState } from 'vuex'
import GenericViewMixin from '@/mixins/GenericViewMixin'
import {
  chargeTypes,
  customerTypes,
  activeClientsRanges,
  pricingModels,
  billingFrequencies,
  invoicingMethods,
  paymentMethods,
  paymentProcessors
} from '@/collections/onboarding'

export default {
  mixins: [GenericViewMixin],
  data: () => ({
    chargeTypes,
    customerTypes,
    activeClientsRanges,
    pricingModels,
    billingFrequencies,
    invoicingMethods,
    paymentMethods,
    paymentProcessors,
    currentStep: 1,
    showError: false,
    errorMessage: ''
  }),
  computed: {
    ...mapState({
      responses: state => state.onboarding.responses
    }),
    showPricingSteps () {
      return this.responses.charge_type === 'recurring' || this.responses.charge_type === 'both'
    },
    showProcessorStep () {
      return this.responses.payment_method === 'card' || this.responses.payment_method === 'both'
    },
    visibleSteps () {
      const steps = []

      steps.push({ key: 'charge_type' })
      steps.push({ key: 'active_clients' })

      if (this.showPricingSteps) {
        steps.push({ key: 'pricing_model' })
        steps.push({ key: 'billing_frequency' })
      }

      steps.push({ key: 'invoicing_method' })
      steps.push({ key: 'payment_method' })

      if (this.showProcessorStep) {
        steps.push({ key: 'payment_processor' })
      }

      return steps
    },
    isLastStep () {
      return this.currentStep === this.visibleSteps.length
    }
  },
  created () {
    const loadProfile = this.$store.dispatch('onboarding/LOAD_USER_PROFILE')
    const loadState = this.$store.state.onboarding.stateLoaded
      ? Promise.resolve()
      : this.$store.dispatch('onboarding/LOAD_STATE')

    Promise.all([loadProfile, loadState]).then(() => {
      if (!this.responses.role || !this.responses.channels.length) {
        this.$router.push({ name: 'UserOnboarding' })
        return
      }

      if (!this.$store.state.auth.account.id) {
        this.$router.push({ name: 'OrganizationCreate' })
        return
      }

      this.currentStep = this.findResumeStep()
    })
  },
  methods: {
    findResumeStep () {
      for (let i = 0; i < this.visibleSteps.length; i++) {
        const key = this.visibleSteps[i].key
        if (key === 'active_clients') {
          if (!this.responses.customer_type || !this.responses.active_clients) {
            return i + 1
          }
        } else if (!this.responses[key]) {
          return i + 1
        }
      }
      return this.visibleSteps.length
    },
    setResponse (key, value) {
      this.showError = false
      this.$store.dispatch('onboarding/SET_RESPONSE', { key, value })
    },
    stepNumber (key) {
      const index = this.visibleSteps.findIndex(s => s.key === key)
      return index >= 0 ? index + 1 : -1
    },
    currentStepKey () {
      const total = this.visibleSteps.length
      if (total === 0) return ''
      if (this.currentStep < 1 || this.currentStep > total) {
        this.currentStep = Math.min(Math.max(1, this.currentStep), total)
      }
      const step = this.visibleSteps[this.currentStep - 1]
      return step ? step.key : ''
    },
    nextStep () {
      const key = this.currentStepKey()

      if (key === 'active_clients') {
        if (!this.responses.customer_type || !this.responses.active_clients) {
          this.showError = true
          this.errorMessage = 'Selecciona una opción en cada pregunta.'
          return
        }
      } else if (!this.responses[key]) {
        this.showError = true
        this.errorMessage = 'Selecciona una de las opciones.'
        return
      }

      this.showError = false
      this.errorMessage = ''

      // Persist progress so it's not lost if user leaves mid-onboarding
      this.$store.dispatch('onboarding/SAVE_STATE')

      this.trackEvent('Onboarding Step Completed', { step: key, answer: this.responses[key] })

      if (this.isLastStep) {
        this.completeOnboarding()
        return
      }

      this.currentStep += 1
    },
    prevStep () {
      this.showError = false
      if (this.currentStep > 1) {
        this.currentStep -= 1
      }
    },
    completeOnboarding () {
      this.$store.dispatch('onboarding/COMPLETE').then(() => {
        if (process.env.NODE_ENV === 'production') {
          this.$analytics.identify(null, {
            role: this.responses.role,
            channels: this.responses.channels.join(';')
          })
        }

        this.trackEvent('Onboarding Completed', {
          path: this.$store.state.onboarding.path,
          ...this.responses
        })
        this.$router.push({ name: 'OrganizationWithPiriod' })
      })
    }
  }
}
</script>

<style scoped>
.v-sheet--outlined.v-sheet--active {
  border: 1px solid #3F51B5 !important;
}

.v-application .elevation-0.v-sheet--active,
.v-application .elevation-3:hover.v-sheet--active {
  box-shadow: 0px 0px 0px 3px #CCE1FF !important;
}
</style>
